import { GridCol, GridContainer, GridRow, Icon, ICONS } from '@bob/library'
import { Asset, AssetProps } from '@websrc/App/Components/Asset/Asset'
import Heading from '@websrc/App/Components/Heading/Heading'
import { spotWrapperProps } from '@websrc/App/Helpers/spotWrapperProps'
import { MtmParams } from '@websrc/App/Hooks/useMTMParams'
import { useReversedLayout } from '@websrc/App/Hooks/useReversedLayout'
import { mapContentSpot } from '@websrc/DataMappers/mapSpot'
import { BackgroundBrick } from '@websrc/Spot/BackgroundBrick/BackgroundBrick'
import { SpotLinkAnchor } from '@websrc/Spot/Link/SpotLinkAnchor'
import React from 'react'

import { LearnMoreButtonType } from '../Collapse/SpotCollapse'
import * as SC from './SpotContent.style'

export interface SpotContentProps {
  spot: ReturnType<typeof mapContentSpot>['content_spot']
  LearnMoreButton?: LearnMoreButtonType
  imageLazy?: boolean
  withinExpandable?: boolean
  isTopOfPage?: boolean
}

/** Private component that renders the actual content */
const Content = ({ spot, LearnMoreButton, imageLazy, isTopOfPage }: SpotContentProps) => {
  const reversedLayout = useReversedLayout(spot.reversed_layout)
  const { combination_of_mtm_params, mtm_params } = spot
  const renderAsset = (asset: AssetProps | undefined) =>
    asset &&
    (asset.video ?? asset.thumbnail) && (
      <GridCol desktop={6} desktopOffset={reversedLayout ? 0 : 1}>
        <Asset
          lazyLoading={imageLazy}
          sizes={`(min-width: 1024px) 600px`}
          srcSetWidths={[320, 400, 600, 800, 1200]}
          {...asset}
          altText={asset.altText}
        />
      </GridCol>
    )

  return (
    <>
      {reversedLayout && renderAsset(spot.asset)}
      <GridCol desktop={5} desktopOffset={reversedLayout ? 1 : 0}>
        <SC.Content>
          <Heading
            description={spot.description}
            headline={spot.headline}
            label={spot.caption}
            level={isTopOfPage ? 1 : 2}
            size={'xl'}
            style={{ gap: '10px', display: 'flex', flexDirection: 'column' }}
          />
          <SC.CTAWrapper>
            {spot.cta_link.cta_text && (
              <SpotLinkAnchor
                combination_of_mtm_params={combination_of_mtm_params}
                ctaType={spot.cta_type}
                link={spot.cta_link}
                linkSection={[spot.caption, spot.headline]}
                mtm_params={mtm_params as MtmParams}
              >
                {spot.cta_type === 'tertiary' && <Icon icon={ICONS.ARROW_RIGHT} size={16} />}
                <span>{spot.cta_link.cta_text}</span>
              </SpotLinkAnchor>
            )}
            {LearnMoreButton && (
              <SC.LearnMoreButtonWrapper isSibling={Boolean(spot.cta_link)}>
                <LearnMoreButton position={[spot.caption, spot.headline]} />
              </SC.LearnMoreButtonWrapper>
            )}
          </SC.CTAWrapper>
        </SC.Content>
      </GridCol>
      {!reversedLayout && renderAsset(spot.asset)}
    </>
  )
}

export const SpotContent = (props: SpotContentProps) => {
  const { spot, withinExpandable } = props
  const reversedLayout = useReversedLayout(spot.reversed_layout)

  if (withinExpandable) {
    return (
      <div>
        <GridRow>
          <Content {...props} />
        </GridRow>
      </div>
    )
  }

  return (
    <GridContainer>
      <div {...spotWrapperProps(spot.caption)} data-testid={'caption'} />
      <div>
        <GridRow>
          <GridCol>
            <GridRow>
              <BackgroundBrick
                desktop={5}
                desktopOffset={reversedLayout ? 0 : 8}
                reversedLayout={reversedLayout}
              />
            </GridRow>
          </GridCol>
          <Content {...props} />
        </GridRow>
      </div>
    </GridContainer>
  )
}
